
import React, { useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Button, InputGroup, Table } from 'react-bootstrap';
import Select from 'react-select';
import config from "../../config/config";
import axios from "axios";
import { toast } from 'react-toastify';
import { getAuthToken } from "../../utils/token";

const TrainingScheduleModal = ({ show, handleClose, trainingId }) => {
  const [trainingDetails, setTrainingDetails] = useState({ info: {}, participants: [] });
  const [loading, setLoading] = useState(false);
  const [trainingOptions, setTrainingOptions] = useState([]);
  const [selectedTrainingOption, setSelectedTrainingOption] = useState(null);
  const [participantSearch, setParticipantSearch] = useState("");

  // Fetch all available trainings for select
  const fetchTrainingOptions = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      setLoading(true);
      const response = await axios.get(`${config.API}/admin/traininglist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const trainings = response?.data?.trainings || [];
      const options = trainings.map(training => ({
        value: training?.id,
        label: training?.name || training?.training_name || "Unnamed"
      }));
      setTrainingOptions(options);

    } catch (err) {
      console.error("Error fetching training list:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch training details for given ID
  const fetchTrainingDetails = async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${config.API}/supervisor/trainingschedule/${trainingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200 && response.data.success) {
        const data = response?.data;
        const info = data?.training?.[0] || {};
        const participants = data?.participant?.map(participant => ({ ...participant, selected: false }));
        setTrainingDetails({ info, participants });

        // Preselect training type in select
        if (info.training_category) {
          const selectedOption = trainingOptions.find(
            option => option?.label === info?.training_category?.name
          );
          setSelectedTrainingOption(selectedOption || null);
        }
      } else {
        setTrainingDetails({ info: {}, participants: [] });
        setSelectedTrainingOption(null);
      }
    } catch (err) {
      console.error("Error fetching training details:", err.message);
      setTrainingDetails({ info: {}, participants: [] });
      setSelectedTrainingOption(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainingOptions();
  }, []);

  useEffect(() => {
    if (trainingId) fetchTrainingDetails();
  }, [trainingId, trainingOptions]);

  // Update training info fields
  const handleTrainingInfoChange = (field, value) => {
    setTrainingDetails(prev => ({
      ...prev,
      info: { ...prev.info, [field]: value }
    }));
  };

  // Update selected training option
  const handleTrainingSelectChange = (option) => {
    setSelectedTrainingOption(option);
    handleTrainingInfoChange('training_category', { name: option?.label || "" });
  };

  // Toggle participant selection
  const toggleParticipantSelection = (participantId) => {
    setTrainingDetails(prev => ({
      ...prev,
      participants: prev.participants.map(participant =>
        participant.participant_id === participantId
          ? { ...participant, selected: !participant.selected }
          : participant
      )
    }));
  };

  // Filter participants by search
  const filteredParticipants = trainingDetails?.participants?.filter(participant =>
    participant?.participant?.name?.toLowerCase().includes(participantSearch.toLowerCase())
  );

  // Submit reschedule
  const handleReschedule = async () => {
    const token = getAuthToken();
    if (!token) return;

    const payload = {
      training_date: trainingDetails?.info?.training_date,
      duration: trainingDetails?.info?.duration_hours,
      participants: trainingDetails?.participants
        .filter(participant => participant?.selected)
        .map(participant => participant?.participant_id)
    };

    try {
      setLoading(true);
      const response = await axios.post(
        `${config.API}/supervisor/trainingschedule/${trainingId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
       toast.success(response?.data?.message);
        handleClose();
      } else {
        alert("Failed to reschedule training");
      }
    } catch (err) {
      console.error("Error rescheduling training:", err.message);
      alert("Error rescheduling training");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered className="font_family">
      <Modal.Body className="p-5">
        <strong className="fs-4">Training Schedule</strong>

        {/* Training Type */}
        <Form.Group className="mb-3 mt-4">
          <Row>
            <Col md={2} className="d-flex align-items-center">
              <Form.Label className="mb-0">Type</Form.Label>
            </Col>
            <Col md={10}>
              <Select
                options={trainingOptions}
                value={selectedTrainingOption}
                onChange={handleTrainingSelectChange}
                placeholder="Select Training"
                isClearable
              />
            </Col>
          </Row>
        </Form.Group>

        {/* Date & Duration */}
        <Row className="mb-3">
          <Col md={6}>
            <Row>
              <Col md={4} className="d-flex align-items-center">
                <Form.Label className="mb-0">Date of Training</Form.Label>
              </Col>
              <Col md={8}>
                <Form.Control
                  type="date"
                  value={trainingDetails?.info?.training_date || ""}
                  onChange={(e) => handleTrainingInfoChange('training_date', e.target.value)}
                />
              </Col>
            </Row>
          </Col>
          <Col md={6}>
            <Row>
              <Col md={4} className="d-flex align-items-center">
                <Form.Label className="mb-0">Duration</Form.Label>
              </Col>
              <Col md={8}>
                <Form.Control
                  type="number"
                  value={trainingDetails?.info?.duration_hours || ""}
                  onChange={(e) => handleTrainingInfoChange('duration_hours', e.target.value)}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Notes */}
        <Form.Group className="mb-3">
          <Row>
            <Col md={2} className="d-flex align-items-start">
              <Form.Label>Notes</Form.Label>
            </Col>
            <Col md={10}>
              <Form.Control
                as="textarea"
                rows={3}
                value={trainingDetails?.info?.notes || ""}
                onChange={(e) => handleTrainingInfoChange('notes', e.target.value)}
              />
            </Col>
          </Row>
        </Form.Group>

        {/* Participants */}
        <Form.Group className="mb-3">
          <Row>
            <Col md={2} className="d-flex align-items-start">
              <Form.Label>Select Participants</Form.Label>
            </Col>
            <Col md={10}>
              <InputGroup className="mb-2">
                <Form.Control
                  placeholder="Search"
                  value={participantSearch}
                  onChange={(e) => setParticipantSearch(e.target.value)}
                />
              </InputGroup>

              <div className="border rounded p-2" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                <Table borderless size="sm" className="mb-0">
                  <tbody>
                    {filteredParticipants?.map(participant => (
                      <tr key={participant?.participant_id} className="align-middle">
                        <td className="text-nowrap">{participant?.participant?.id}</td>
                        <td className="text-nowrap">{participant?.participant?.name}</td>
                        <td className="text-nowrap">
                          Section: <strong>Rubber</strong>
                        </td>
                        <td className="text-end">
                          <Form.Check
                            type="checkbox"
                            checked={participant?.selected || false}
                            onChange={() => toggleParticipantSelection(participant?.participant_id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="dark" onClick={handleReschedule}>
          Reschedule
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TrainingScheduleModal;
